// The only place fetch is called (IMPLEMENTATION_PLAN.md §11.3).
//
// These interfaces mirror the server contracts in Darkroom.Api/Contracts/Contracts.cs exactly,
// in snake_case, because the host applies JsonNamingPolicy.SnakeCaseLower once for everything.
// There is deliberately no mapping layer: a field renamed on the server is a type error here.

// ── Wire types ─────────────────────────────────────────────────────────────────────────────

export type GenerationMode = "generate" | "edit";
export type GenerationStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";
export type ImageOrigin = "generated" | "uploaded";
export type ParameterKind = "enum" | "integer" | "number" | "boolean" | "text" | "seed";
export type ParameterGroup = "output" | "quality" | "content" | "advanced";

export interface Page<T> {
  items: T[];
  next_cursor?: string;
}

export type UserRole = "admin" | "user";

export interface SessionUser {
  id: string;
  email: string;
  display_name: string;
  picture_url?: string;
  role: UserRole;
  is_admin: boolean;
}

export interface Session {
  authenticated: boolean;
  user?: SessionUser;
}

export interface ManagedUser {
  id: string;
  email: string;
  display_name: string;
  role: UserRole;
  is_active: boolean;
  /** In Auth__AllowedEmails: forced to admin on every sign-in, so role/status are read-only. */
  is_from_allowlist: boolean;
  created_at: string;
  last_seen_at: string;
}

// ── Spend ──────────────────────────────────────────────────────────────────────────────────

export interface ModelSpend {
  model_id: string;
  model_display_name: string;
  provider_id: string;
  generation_count: number;
  micro_usd: number;
}

export interface BoardSpend {
  /** Absent on the one aggregate row for spend whose board has since been deleted. */
  board_id?: string;
  title: string;
  generation_count: number;
  micro_usd: number;
}

export interface UserSpend {
  user_id: string;
  email: string;
  display_name: string;
  role: UserRole;
  is_active: boolean;
  generation_count: number;
  micro_usd: number;
  window_micro_usd: number;
  last_seen_at: string;
}

export interface DailySpend {
  /** ISO date, a UTC day bucket. */
  date: string;
  micro_usd: number;
  generation_count: number;
}

export interface SpendSummary {
  currency: string;
  window_days: number;
  total_micro_usd: number;
  window_micro_usd: number;
  generation_count: number;
  window_generation_count: number;
  failed_count: number;
  /** Generations whose model had no usable published price. Always shown, never folded in. */
  unpriced_generation_count: number;
  by_model: ModelSpend[];
  by_board: BoardSpend[];
  daily: DailySpend[];
}

export interface OrgSpend {
  currency: string;
  window_days: number;
  total_micro_usd: number;
  window_micro_usd: number;
  generation_count: number;
  window_generation_count: number;
  unpriced_generation_count: number;
  by_user: UserSpend[];
  by_model: ModelSpend[];
  daily: DailySpend[];
}

export interface ParameterOption {
  value: string;
  label: string;
  hint?: string;
  /** "w:h". When present the control draws a swatch in the true proportion. */
  ratio?: string;
}

export interface VisibleWhen {
  key: string;
  equals_any: string[];
}

export interface Parameter {
  key: string;
  label: string;
  kind: ParameterKind;
  description?: string;
  group: ParameterGroup;
  default?: string | number | boolean | null;
  options?: ParameterOption[];
  min?: number;
  max?: number;
  step?: number;
  max_length?: number;
  applies_to: GenerationMode[];
  visible_when?: VisibleWhen;
  advanced: boolean;
  /** Documented but unconfirmed against the provider's official reference. Badged in the UI. */
  experimental: boolean;
}

export interface Capabilities {
  supports_generate: boolean;
  supports_edit: boolean;
  supports_mask: boolean;
  max_input_images: number;
  max_outputs: number;
  returns_text: boolean;
  accepted_input_mime_types: string[];
  max_input_bytes: number;
}

export interface Model {
  id: string;
  provider_id: string;
  display_name: string;
  family?: string;
  description?: string;
  tags: string[];
  docs_url?: string;
  pricing_note?: string;
  deprecation_note?: string;
  capabilities: Capabilities;
  parameters: Parameter[];
}

export interface Provider {
  id: string;
  display_name: string;
  docs_url?: string;
  configured: boolean;
  unavailable_reason?: string;
  models: Model[];
}

export interface Catalog {
  providers: Provider[];
}

export interface Board {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface BoardSummary extends Board {
  generation_count: number;
  cover_image_id?: string;
  cover_thumb_url?: string;
  last_model_id?: string;
}

export interface ImageAsset {
  id: string;
  origin: ImageOrigin;
  content_type: string;
  width: number;
  height: number;
  byte_size: number;
  created_at: string;
  generation_id?: string;
  board_id?: string;
  prompt?: string;
  model_id?: string;
  content_url: string;
  thumb_url?: string;
}

export interface GenerationInput {
  image_id: string;
  role: "image" | "mask";
  ordinal: number;
  /** The asset itself, so a frame can be recycled without fetching each reference again. */
  image?: ImageAsset;
}

export type ParameterValues = Record<string, string | number | boolean>;

export interface Generation {
  id: string;
  board_id: string;
  parent_generation_id?: string;
  /** Per-board frame number. How a user refers to one: "from frame 02". */
  sequence: number;
  provider_id: string;
  model_id: string;
  model_display_name: string;
  mode: GenerationMode;
  prompt: string;
  parameters: ParameterValues;
  status: GenerationStatus;
  error_title?: string;
  error_detail?: string;
  response_text?: string;
  provider_metadata?: Record<string, unknown>;
  inputs: GenerationInput[];
  outputs: ImageAsset[];
  created_at: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  /** Estimated spend in millionths of a dollar. Absent when the model is unpriced. */
  cost_micro_usd?: number;
  cost_basis?: string;
}

export interface BoardDetail {
  board: Board;
  generations: Generation[];
}

export interface CreateGenerationRequest {
  model_id: string;
  mode: GenerationMode;
  prompt: string;
  parameters: ParameterValues;
  input_image_ids: string[];
  mask_image_id?: string;
  parent_generation_id?: string;
}

// ── Transport ──────────────────────────────────────────────────────────────────────────────

/** Every server failure is a ProblemDetails document; this is its typed form. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  /** Per-parameter messages from the composer's validation, keyed by parameter key. */
  readonly errors?: Record<string, string>;

  constructor(status: number, title: string, detail?: string, errors?: Record<string, string>) {
    super(title);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.errors = errors;
  }
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string>;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Sent as-is. Used only by the multipart upload. */
  formData?: FormData;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, formData } = options;

  const init: RequestInit = {
    method,
    // Same-origin: the SPA is served from wwwroot by the same process as the API, so there is
    // no CORS story and no cross-site cookie to worry about.
    credentials: "same-origin",
    signal,
  };

  if (formData) {
    init.body = formData;
  } else if (body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(body);
  }

  const response = await fetch(path, init);

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    let problem: ProblemDetails = {};
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      // A non-JSON error body means something upstream failed before the app saw the request.
    }

    throw new ApiError(
      response.status,
      problem.title ?? defaultTitleFor(response.status),
      problem.detail,
      problem.errors,
    );
  }

  return (await response.json()) as T;
}

function defaultTitleFor(status: number): string {
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 403) return "You do not have access to that.";
  if (status === 404) return "Not found.";
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  if (status >= 500) return "Darkroom had a server error.";
  return `Request failed (${status}).`;
}

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

// ── Endpoints ──────────────────────────────────────────────────────────────────────────────

export const api = {
  session: (signal?: AbortSignal) => request<Session>("/api/session", { signal }),

  signInUrl: (returnUrl: string) =>
    `/api/auth/google/start${query({ return_url: returnUrl })}`,

  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  catalog: (signal?: AbortSignal) => request<Catalog>("/api/catalog", { signal }),

  listBoards: (cursor?: string, signal?: AbortSignal) =>
    request<Page<BoardSummary>>(`/api/boards${query({ cursor })}`, { signal }),

  createBoard: (title?: string) =>
    request<Board>("/api/boards", { method: "POST", body: { title } }),

  getBoard: (id: string, signal?: AbortSignal) =>
    request<BoardDetail>(`/api/boards/${id}`, { signal }),

  renameBoard: (id: string, title: string) =>
    request<Board>(`/api/boards/${id}`, { method: "PATCH", body: { title } }),

  /** `confirm` must be the board title's slug; the server checks it. */
  deleteBoard: (id: string, confirm: string) =>
    request<void>(`/api/boards/${id}${query({ confirm })}`, { method: "DELETE" }),

  createGeneration: (boardId: string, body: CreateGenerationRequest) =>
    request<Generation>(`/api/boards/${boardId}/generations`, { method: "POST", body }),

  pollGenerations: (ids: string[], signal?: AbortSignal) =>
    ids.length === 0
      ? Promise.resolve<Generation[]>([])
      : request<Generation[]>(`/api/generations${query({ ids: ids.join(",") })}`, { signal }),

  retryGeneration: (id: string) =>
    request<Generation>(`/api/generations/${id}/retry`, { method: "POST" }),

  deleteGeneration: (id: string) =>
    request<void>(`/api/generations/${id}`, { method: "DELETE" }),

  uploadImage: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<ImageAsset>("/api/images", { method: "POST", formData: form });
  },

  listImages: (
    params: {
      cursor?: string;
      q?: string;
      model_id?: string;
      board_id?: string;
      origin?: ImageOrigin;
      limit?: number;
    } = {},
    signal?: AbortSignal,
  ) => request<Page<ImageAsset>>(`/api/images${query(params)}`, { signal }),

  deleteImage: (id: string) =>
    request<void>(`/api/images/${id}${query({ confirm: "delete" })}`, { method: "DELETE" }),

  // Spend. `me` is available to anyone signed in; `overview` is admin-only and returns 403.
  mySpend: (days = 30, signal?: AbortSignal) =>
    request<SpendSummary>(`/api/analytics/me${query({ days })}`, { signal }),

  orgSpend: (days = 30, signal?: AbortSignal) =>
    request<OrgSpend>(`/api/analytics/overview${query({ days })}`, { signal }),

  // Accounts. Admin only.
  listUsers: (signal?: AbortSignal) =>
    request<ManagedUser[]>("/api/admin/users", { signal }),

  createUser: (email: string, role: UserRole) =>
    request<ManagedUser>("/api/admin/users", { method: "POST", body: { email, role } }),

  updateUser: (id: string, changes: { role?: UserRole; is_active?: boolean }) =>
    request<ManagedUser>(`/api/admin/users/${id}`, { method: "PATCH", body: changes }),
};

// ── Formatters ─────────────────────────────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds % 60)}s`;
}

/** mm:ss, for the elapsed timer on a running generation. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(iso);
}

/**
 * Micro-dollars to a readable amount. Sub-cent figures are the norm for a single image, so a
 * flat two decimal places would show most generations as $0.00 and make the number useless.
 */
export function formatMicroUsd(micro: number | null | undefined): string {
  if (micro === null || micro === undefined) return "—";
  const usd = micro / 1_000_000;
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 100) return `$${usd.toFixed(2)}`;
  return `$${usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** Frame numbers are always two digits: 01, 02 … 14. */
export function formatFrame(sequence: number): string {
  return String(sequence).padStart(2, "0");
}

/** Parses a "w:h" ratio, tolerating the decimal forms xAI uses (9:19.5). */
export function parseRatio(ratio: string | undefined): number | null {
  if (!ratio) return null;
  const [w, h] = ratio.split(":").map(Number);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return w / h;
}

export function isInFlight(status: GenerationStatus): boolean {
  return status === "queued" || status === "running";
}
